/**
 * A caixa de revisão, portada de knowledge.js:142-253.
 *
 * O que esta tela existe para impedir: agente escrevendo direto no acervo. A IA
 * propõe, o dono do módulo aprova, e a versão publicada leva a autoria dos dois.
 * Por isso o corpo do cartão é um lado-a-lado (o que está no acervo × o que foi
 * proposto) com o texto proposto editável antes de aprovar — aprovar sem poder
 * corrigir uma vírgula empurra o revisor a rejeitar tudo.
 *
 * Rejeitar exige motivo porque o motivo é o retorno que o agente lê na próxima
 * tentativa; rejeição muda é proposta que volta igual amanhã.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { postar } from '../api/cliente';
import { chaves, usePropostaAberta, usePropostas, useSessoes } from '../api/consultas';
import type { Proposta } from '../api/tipos';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { useRecado } from '../componentes/Recado';
import { ESPECIE_DE_PROPOSTA, SITUACAO_DA_PROPOSTA, deAgente, rotulo, shaCurto } from '../dados/rotulos';
import { md } from '../md/markdown';

type Filtro = 'pending' | 'approved' | 'rejected' | 'all';

const FILTROS: [Filtro, string][] = [
  ['pending', 'pendentes'],
  ['approved', 'aprovadas'],
  ['rejected', 'rejeitadas'],
  ['all', 'todas'],
];

export function Revisao({ slug }: { slug: string }) {
  const navegar = useNavigate();
  const avisar = useRecado();
  const propostas = usePropostas(slug);
  const sessoes = useSessoes(slug);
  const [filtro, setFiltro] = useState<Filtro>('pending');
  const [aberta, setAberta] = useState<string | null>(null);

  if (propostas.isPending) return <Carregando rotulo="a caixa de revisão" />;
  if (propostas.error) {
    return <Falha erro={propostas.error} rotulo="a caixa de revisão" aoRepetir={propostas.refetch} />;
  }

  const lista = propostas.data?.proposals || [];
  const pendentes = propostas.data?.pending || 0;
  const naoConfirmadas = sessoes.data?.unconfirmed || [];
  const visiveis = lista.filter((p) => (filtro === 'all' ? true : p.status === filtro));

  return (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>revisão</div>
      <div className="kb-head">
        <div>
          <div className="sec-title">
            Caixa de revisão · {pendentes} pendente{pendentes === 1 ? '' : 's'}
          </div>
          <div className="sec-sub">
            A IA no Cursor propõe correções e decisões; o texto só entra no acervo quando o dono ou
            um admin aprova. Aprovar publica uma nova versão com a autoria dos dois.
          </div>
        </div>
        <div className="kb-actions">
          <div className="seg" id="rev-filter">
            {FILTROS.map(([v, l]) => (
              <button key={v} type="button" className={filtro === v ? 'on' : ''} onClick={() => setFiltro(v)}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* a fila de sessões falhando não pode esconder as propostas que já vieram */}
      {!!sessoes.error && (
        <div className="warn-line">
          Não deu para ler os resumos de sessão que esperam confirmação — a contagem desta fila pode
          estar incompleta.
        </div>
      )}

      {!!naoConfirmadas.length && (
        <div className="warn-line">
          {naoConfirmadas.length} resumo(s) de sessão também esperam você:{' '}
          <button type="button" className="lnkbtn" onClick={() => navegar(`/p/${slug}/sessions`)}>
            abrir Sessões
          </button>
          . Confirmar um resumo o promove de “não confirmado” para memória do time.
        </div>
      )}

      {visiveis.length
        ? visiveis.map((p) => (
          <CartaoDeProposta
            key={p.id}
            slug={slug}
            p={p}
            aberta={aberta === p.id}
            aoAlternar={() => setAberta(aberta === p.id ? null : p.id)}
          />
        ))
        : filtro === 'pending'
          ? (
            <Vazio
              titulo="Nada esperando revisão"
              acao={{
                rotulo: 'Propor uma correção eu mesmo',
                ao: () => {
                  navegar(`/p/${slug}/doc`);
                  avisar('abra um arquivo e use "propor correção" no cabeçalho');
                },
              }}
            >
              Esta fila enche sozinha quando alguém trabalha com o agente conectado: ao encontrar doc
              errada ou desatualizada, ele manda a correção para cá em vez de escrever direto no
              acervo. No Cursor, peça <i>“revise a doc deste arquivo e proponha a correção”</i>.
            </Vazio>
          )
          : (
            <Vazio
              titulo={`Nenhuma proposta ${
                filtro === 'approved' ? 'aprovada' : filtro === 'rejected' ? 'rejeitada' : 'registrada'
              }`}
            >
              Troque o filtro acima para ver as outras. Toda proposta fica no histórico, com quem
              decidiu e por quê.
            </Vazio>
          )}
    </>
  );
}

function CartaoDeProposta({ slug, p, aberta, aoAlternar }: {
  slug: string;
  p: Proposta;
  aberta: boolean;
  aoAlternar: () => void;
}) {
  return (
    <div className={`prop ${p.status}${aberta ? ' open' : ''}`} data-prop={p.id}>
      <div className="prop-head" aria-expanded={aberta} {...acionavel(aoAlternar)}>
        <span className={`tag ${p.kind}`}>{rotulo(ESPECIE_DE_PROPOSTA, p.kind)}</span>
        <span className={`tag ${p.status}`}>{rotulo(SITUACAO_DA_PROPOSTA, p.status)}</span>
        {deAgente(p.origin) && <span className="tag ia">IA</span>}
        <span className="unit">{p.unit_key}</span>
        <span className="who">
          {p.origin || '—'} · {(p.created_at || '').slice(0, 10)}
          {p.result_version ? ` · publicada v${p.result_version}` : ''}
        </span>
      </div>
      <div className="prop-body">
        {aberta && <CorpoDaProposta slug={slug} id={p.id} />}
      </div>
    </div>
  );
}

function CorpoDaProposta({ slug, id }: { slug: string; id: string }) {
  const avisar = useRecado();
  const navegar = useNavigate();
  const fila = useQueryClient();
  const consulta = usePropostaAberta(slug, id);
  const [nota, setNota] = useState('');
  const [texto, setTexto] = useState<string | null>(null);

  const decidir = useMutation({
    mutationFn: async (acao: 'approve' | 'reject') => {
      const d = consulta.data!;
      // só manda o texto quando o revisor mexeu nele: corpo igual ao proposto
      // faria o servidor registrar edição que não houve
      const corpo = acao === 'approve'
        ? {
          review_note: nota.trim() || null,
          body_md: texto !== null && texto.trim() !== d.body_md.trim() ? texto : undefined,
        }
        : { review_note: nota.trim() || null };
      return postar<{ result_version?: number }>(
        `/api/${slug}/proposals/${encodeURIComponent(id)}/${acao}`,
        corpo,
      );
    },
    onSuccess: async (r, acao) => {
      avisar(acao === 'approve'
        ? `publicada v${r.result_version} — já vale para o time e para os agentes`
        : 'proposta rejeitada com retorno');
      await Promise.all([
        fila.invalidateQueries({ queryKey: chaves.propostas(slug) }),
        fila.invalidateQueries({ queryKey: chaves.proposta(slug, id) }),
        fila.invalidateQueries({ queryKey: chaves.docs(slug) }),
      ]);
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  if (consulta.isPending) return <Carregando rotulo="carregando a proposta e o texto atual do acervo…" />;
  if (consulta.error) {
    const msg = consulta.error instanceof Error ? consulta.error.message : String(consulta.error);
    return <div className="warn-line">Não deu para abrir esta proposta: {msg}</div>;
  }

  const d = consulta.data!;
  const baseMd = d.base?.body_md || '';
  const podeDecidir = !!d.can_review && d.status === 'pending';
  const abrirAlvo = (chave: string) => navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`);

  return (
    <>
      {d.rationale && <div className="prop-why"><b>Por que mudar:</b> {d.rationale}</div>}
      {d.stale_base && (
        <div className="warn-line">
          ⚠ A doc mudou depois desta proposta (base v{d.base_version}, acervo v{d.base?.version}).
          Reveja antes de aprovar.
        </div>
      )}
      {d.owner?.orphan && (
        <div className="warn-line">
          Este arquivo não tem dono declarado — qualquer membro pode decidir. Declare donos na aba Saúde.
        </div>
      )}

      <div className="sbs">
        <div className="col">
          <div className="col-t">
            no acervo {d.base ? `(v${d.base.version} · @${shaCurto(d.base.source_sha)})` : '(unidade nova)'}
          </div>
          {baseMd
            ? <div className="col-b" dangerouslySetInnerHTML={{ __html: md(baseMd) }} />
            : <div className="col-b"><span className="muted">nada publicado ainda nesta unidade</span></div>}
        </div>
        <div className="col">
          <div className="col-t">proposto {podeDecidir ? '· edite antes de aprovar' : ''}</div>
          {podeDecidir
            ? (
              <textarea
                aria-label="texto proposto"
                value={texto ?? d.body_md}
                onChange={(e) => setTexto(e.target.value)}
              />
            )
            : <div className="col-b" dangerouslySetInnerHTML={{ __html: md(d.body_md) }} />}
        </div>
      </div>

      <details style={{ margin: '8px 0' }}>
        <summary className="muted" style={{ cursor: 'pointer', fontSize: 12.5 }}>
          ver diff linha a linha (+{d.diff.added} −{d.diff.removed})
        </summary>
        <div className="col" style={{ marginTop: 8 }}>
          <div className="col-b diffv">
            <pre className="diff">
              {(d.diff.lines || []).map((x, i) => (
                <div key={i} className={`dl${x.t === '+' ? ' add' : x.t === '-' ? ' del' : ''}`}>
                  <span className="dl-g">{x.t}</span>{x.l}
                </div>
              ))}
            </pre>
          </div>
        </div>
      </details>

      {!!d.links?.length && (
        <div className="tl-links">
          {d.links.map((l) => (
            <span key={l} className="lnk" title={l} {...acionavel(() => abrirAlvo(l), true)}>
              {l}
            </span>
          ))}
        </div>
      )}

      <div className="prop-acts">
        {podeDecidir
          ? (
            <>
              <input
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="comentário da revisão (opcional; se rejeitar, é o retorno para o agente)"
              />
              <button
                type="button"
                className="btn ok"
                disabled={decidir.isPending}
                onClick={(e) => { e.stopPropagation(); decidir.mutate('approve'); }}
              >
                {decidir.isPending && decidir.variables === 'approve' ? 'publicando…' : 'Aprovar e publicar'}
              </button>
              <button
                type="button"
                className="btn bad ghost"
                disabled={decidir.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!nota.trim()) { avisar('diga o motivo — é o retorno que o agente lê'); return; }
                  decidir.mutate('reject');
                }}
              >
                {decidir.isPending && decidir.variables === 'reject' ? 'rejeitando…' : 'Rejeitar'}
              </button>
            </>
          )
          : (
            <span className="muted grow">
              {d.status === 'pending'
                ? 'Só o dono do módulo ou um admin decide esta proposta.'
                : `${rotulo(SITUACAO_DA_PROPOSTA, d.status)} por ${d.reviewer_email || '—'}${
                  d.review_note ? ` — “${d.review_note}”` : ''
                }`}
            </span>
          )}
        <button
          type="button"
          className="btn ghost sm"
          onClick={(e) => { e.stopPropagation(); abrirAlvo(d.unit_key); }}
        >
          abrir unidade
        </button>
      </div>
    </>
  );
}
